'use client';

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { usePathname } from 'next/navigation';
import { PageHomeSchema } from '@/sanity/pages/PageHomeTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';
import ScrollingBanner from '../global/ScrollingBanner';
import { NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';

const CITIES_I18N: Record<
  string,
  { name: string; left: number; top: number }[]
> = {
  en: [
    { name: 'Los Angeles, USA', left: 13.7, top: 42.1 },
    { name: 'New York, USA', left: 26.7, top: 39.4 },
    { name: 'São Paulo, Brazil', left: 32.3, top: 78.4 },
    { name: 'Frankfurt, Germany', left: 52.1, top: 32.3 },
    { name: 'Cape Town, South Africa', left: 52.7, top: 84.6 },
    { name: 'Dubai, UAE', left: 60.1, top: 51.2 },
    { name: 'Sydney, Australia', left: 89.5, top: 82.9 },
  ],
  de: [
    { name: 'Los Angeles, USA', left: 13.7, top: 42.1 },
    { name: 'New York, USA', left: 26.7, top: 39.4 },
    { name: 'São Paulo, Brasilien', left: 32.3, top: 78.4 },
    { name: 'Frankfurt, Deutschland', left: 52.1, top: 32.3 },
    { name: 'Kapstadt, Südafrika', left: 52.7, top: 84.6 },
    { name: 'Dubai, VAE', left: 60.1, top: 51.2 },
    { name: 'Sydney, Australien', left: 89.5, top: 82.9 },
  ],
  br: [
    { name: 'Los Angeles, EUA', left: 13.7, top: 42.1 },
    { name: 'Nova York, EUA', left: 26.7, top: 39.4 },
    { name: 'São Paulo, Brasil', left: 32.3, top: 78.4 },
    { name: 'Frankfurt, Alemanha', left: 52.1, top: 32.3 },
    { name: 'Cidade do Cabo, África do Sul', left: 52.7, top: 84.6 },
    { name: 'Dubai, EAU', left: 60.1, top: 51.2 },
    { name: 'Sydney, Austrália', left: 89.5, top: 82.9 },
  ],
};

function IQBadgeLogo() {
  return (
    <svg
      viewBox="0 0 12 9.3335"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[1.125rem] max-lg:h-[1.125em] w-auto"
    >
      <path d="M0 9.33333V0H2.66667V9.33333H0Z" fill="white" />
      <path
        d="M10.0913 9.33333L9.47266 8.54856C9.30547 8.63543 9.0937 8.70204 8.84291 8.75127C8.59213 8.7976 8.31348 8.82366 8.00697 8.82366C7.19889 8.82366 6.49112 8.63833 5.88924 8.27055C5.28736 7.90278 4.82201 7.38442 4.49321 6.71838C4.1644 6.05233 4 5.28783 4 4.42197C4 3.55611 4.15326 2.82925 4.45698 2.1632C4.76071 1.49716 5.21491 0.97011 5.814 0.582066C6.4131 0.194022 7.14316 0 8.00697 0C8.87078 0 9.59805 0.194022 10.1916 0.582066C10.7879 0.97011 11.2365 1.49716 11.543 2.1632C11.8467 2.82925 12 3.58217 12 4.42197C12 5.20674 11.8746 5.88727 11.621 6.46065C11.3703 7.03692 11.0136 7.52053 10.5566 7.91726L11.6545 9.33333H10.0857H10.0913ZM8.00975 7.13248C8.09335 7.13248 8.17137 7.12669 8.23824 7.11511C8.30791 7.10353 8.372 7.08615 8.4333 7.06009L7.32428 5.6556H8.89307L9.39742 6.34481C9.57297 6.14789 9.70951 5.89595 9.80982 5.58899C9.91014 5.28493 9.95751 4.89399 9.95751 4.41617C9.95751 3.93836 9.89063 3.48371 9.75688 3.0725C9.62313 2.66129 9.41414 2.32537 9.12713 2.06764C8.84013 1.80991 8.46952 1.68249 8.01254 1.68249C7.55556 1.68249 7.17102 1.8128 6.88401 2.06764C6.597 2.32537 6.38802 2.66129 6.25427 3.0725C6.12052 3.48371 6.05364 3.93257 6.05364 4.41617C6.05364 4.89978 6.1233 5.33706 6.25984 5.74827C6.39638 6.15948 6.60815 6.4925 6.89516 6.74734C7.18217 7.00217 7.55277 7.12669 8.00975 7.12669V7.13248Z"
        fill="white"
      />
    </svg>
  );
}

function CityPin({
  name,
  left,
  top,
  index,
}: {
  name: string;
  left: number;
  top: number;
  index: number;
}) {
  return (
    // outer div: handles positioning only
    <div
      className="absolute"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        transform: 'translate(-50%, -100%)',
      }}
    >
      {/* inner motion.div: handles popup animation, origin at bottom */}
      <motion.div
        className="flex flex-col items-center"
        initial={{ scale: 0, y: 6, opacity: 0 }}
        whileInView={{ scale: 1, y: 0, opacity: 1 }}
        viewport={{ once: true, amount: 0 }}
        transition={{
          type: 'spring',
          stiffness: 380,
          damping: 18,
          delay: index * 0.08,
        }}
        style={{ transformOrigin: 'bottom center' }}
      >
        {/* Speech bubble */}
        <div className="relative pb-[1.025rem] max-lg:pb-[1.025em]">
          <span className="whitespace-nowrap rounded-full bg-white px-[1.875rem] py-[1rem] font-geist text-[1.5rem] font-medium leading-[0.9] text-black max-lg:px-[1.25em] max-lg:py-[0.6667em] max-lg:text-[1.5em]">
            {name}
          </span>
        </div>

        {/* IQ badge */}
        <div className="flex h-[2.5rem] w-[2.75rem] items-center justify-center rounded-[0.75rem] bg-[#1aa87f] max-lg:h-[2.5em] max-lg:w-[2.75em] max-lg:rounded-[0.75em]">
          <IQBadgeLogo />
        </div>
      </motion.div>
    </div>
  );
}

export default function WorldMapSection({ data }: { data: PageHomeSchema }) {
  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;
  const cities = CITIES_I18N[language] ?? CITIES_I18N[DEFAULT_LANGUAGE];

  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let cancelled = false;
    const wapiAnimations: Animation[] = [];

    fetch('/global/world-map.svg')
      .then((r) => r.text())
      .then((svgText) => {
        if (cancelled || !mapRef.current) return;
        mapRef.current.innerHTML = svgText;
        const svg = mapRef.current.querySelector('svg');
        if (!svg) return;
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        svg.style.display = 'block';

        const greenDots = Array.from(
          svg.querySelectorAll('path[fill="#1aa87f"]'),
        ) as SVGElement[];

        greenDots.forEach((dot) => {
          if (cancelled) return;
          const anim = dot.animate(
            [{ opacity: '0.15' }, { opacity: '1' }, { opacity: '0.15' }],
            {
              duration: 1000 + Math.random() * 2000,
              iterations: Infinity,
              delay: Math.random() * 2000,
              easing: 'ease-in-out',
              fill: 'both',
            },
          );
          wapiAnimations.push(anim);
        });
      });

    return () => {
      cancelled = true;
      wapiAnimations.forEach((a) => a.cancel());
    };
  }, []);

  if (!data.worldMapChip && !data.worldMapTitle) return null;

  return (
    <section className="w-full bg-[#f4f5f5]">
      {/* Header */}
      <div className="mx-auto flex w-[min(86rem,calc(100%-4rem))] flex-col items-center gap-[2rem] lg:pb-[8.88rem] pb-[4rem] lg:pt-[10rem] pt-[5rem] text-center">
        {data.worldMapChip && (
          <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
            {data.worldMapChip}
          </p>
        )}
        {data.worldMapTitle && (
          <div className="richtext flex flex-col gap-[2rem]">
            <PortableText
              value={data.worldMapTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {/* Map */}
      <div className="relative mx-auto hidden w-[min(147.25rem,calc(100%-4rem))] pb-[18rem] lg:block">
        <div className="relative w-full" style={{ aspectRatio: '1425 / 699' }}>
          <div ref={mapRef} className="absolute inset-0" />
          {cities.map((city, i) => (
            <CityPin key={city.name + language} {...city} index={i} />
          ))}
        </div>
      </div>

      <div className="mx-auto w-[min(100rem,calc(100%-4rem))] pb-[4rem] lg:hidden" data-home-mobile-map>
        <div className="relative w-full [container-type:inline-size]" style={{ aspectRatio: '1425 / 699' }}>
          <div className="absolute inset-0 bg-[url('/global/world-map.svg')] bg-contain bg-center bg-no-repeat" />
          <div className="absolute inset-0 [&>div]:[font-size:1cqw]" style={{ aspectRatio: '1425 / 699' }}>
            {cities.map((city, i) => (
              <CityPin key={city.name + language} {...city} index={i} />
            ))}
          </div>
        </div>
      </div>

      <ScrollingBanner
        className=" border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />
    </section>
  );
}
